"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { retry, wait } from "../../lib/utils";

export type PortalProps = {
  children: React.ReactNode;
  id: string;
  selector?: string;
  emptyTarget?: boolean;
};

export function Portal<ElementType extends Element>({ children, id, selector, emptyTarget }: PortalProps) {
  const [portalElement, setPortalElement] = useState<ElementType | null>(null);

  useEffect(() => {
    retry(
      async () => {
        let element = document.getElementById(id) as ElementType | null;

        if (selector) {
          element = (element?.querySelector(selector) as ElementType | null) ?? null;
        }

        if (!element) {
          throw new Error(`Portal target "${id}" not found`);
        }

        if (emptyTarget) {
          element.innerHTML = "";
          await wait(25);
        }

        setPortalElement(element);
      },
      { attempts: 10, millis: 300, failSilently: true }
    )();
  }, [emptyTarget, id, selector]);

  if (!portalElement) {
    return null;
  }

  return createPortal(children, portalElement);
}


